import { useState } from "react";
import { ConfigProvider, DatePicker } from "antd";
import { CalendarOutlined, DownOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useIsDarkTheme } from "../../../../store/hooks";
import { addDays, parseDateKey } from "../../logic/dayTime";
import {
  DAY_TILE_COUNT,
  FORM_MINUTES,
  firstDateKeyOf,
  isSlotPast,
  type FormSlot,
} from "../../logic/newReservation";
import { isSlotInHours, shiftDateOf, shiftHoursOf, type ShiftHour } from "../../logic/workingSlots";
import { useReservationFormat } from "../../hooks/useReservationFormat";
import { useDatePickerLocale } from "../../hooks/useDatePickerLocale";
import { useEscapeToClose } from "../../hooks/useEscapeToClose";
import { SectionLabel } from "./formPrimitives";
import { tileClass } from "./formStyles";

const pad = (value: number) => String(value).padStart(2, "0");

const isWeekend = (dateKey: string) => [0, 6].includes(parseDateKey(dateKey).getDay());

/**
 * „КОГА": 7 плочки с дни (Днес/Утре/Нд…), „Друга дата…" (календар),
 * часовете на смяната (при работа след полунощ — и нощните, на следващата
 * дата) и минутите :00/:15/:30/:45.
 * Миналите часове/минути на днешния ден и минутите извън работното време са
 * неактивни; почивен ден е без часове, с ред „Почивен ден". Избран час, който
 * мине, докато се попълва формата, остава видимо избран, но зачертан, с ред
 * „Избраният час мина".
 * При редакция (`originalSlot`) границата е началото на резервацията, ако е
 * по-рано от „сега": оригиналният час и всичко след него са активни.
 */
const WhenSection = ({
  slot,
  todayKey,
  nowMs,
  slotPast,
  slotOutsideHours,
  originalSlot,
  onDayChange,
  onHourChange,
  onMinuteChange,
}: {
  slot: FormSlot;
  todayKey: string;
  nowMs: number;
  // Избраният слот мина (формата е отворена от по-рано) — „Създай" е спрян.
  slotPast: boolean;
  // Почивен ден или час извън работното време — „Създай" е спрян.
  slotOutsideHours: boolean;
  // Началото на редактираната резервация (null — нова резервация).
  originalSlot: FormSlot | null;
  onDayChange: (dateKey: string) => void;
  // Часът от смяната и датата му (след полунощ — следващата).
  onHourChange: (hour: number, dateKey: string) => void;
  onMinuteChange: (minute: number) => void;
}) => {
  const { t } = useTranslation();
  const format = useReservationFormat();
  const isDark = useIsDarkTheme();
  const pickerLocale = useDatePickerLocale();
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Esc при отворен календар затваря само него (не и панела).
  useEscapeToClose(calendarOpen, () => setCalendarOpen(false));

  const dayKeys = Array.from({ length: DAY_TILE_COUNT }, (_, index) => addDays(todayKey, index));
  const isOtherDate = !dayKeys.includes(slot.dateKey);
  const selectedHour = Math.floor(slot.minute / 60);
  const selectedMinute = slot.minute % 60;
  // Часовете са на СМЯНАТА на избрания час: при работа след полунощ
  // (петък 12:00–06:00) — 12…23 и 00…05 заедно; часът след полунощ е в
  // съботата и плочката горе минава на нея.
  const shiftHours = shiftHoursOf(shiftDateOf(slot.dateKey, slot.minute));
  const dayClosed = shiftHours.length === 0;
  const nightHours = shiftHours.filter((entry) => entry.dateKey !== shiftHours[0]?.dateKey);
  // Слотът се избира: в работното време и не е минал.
  const isFreeSlot = (dateKey: string, minute: number) =>
    isSlotInHours(dateKey, minute) && !isSlotPast(dateKey, minute, nowMs, originalSlot);
  // Часът е неактивен, когато няма нито един свободен слот в него.
  const isHourDisabled = (entry: ShiftHour) =>
    !FORM_MINUTES.some((minute) => isFreeSlot(entry.dateKey, entry.hour * 60 + minute));
  const isSelectedHour = (entry: ShiftHour) => entry.hour === selectedHour && entry.dateKey === slot.dateKey;

  const dayLabel = (dateKey: string, index: number) => {
    if (index === 0) return t("reservations.form.today");
    if (index === 1) return t("reservations.form.tomorrow");
    return format.shortWeekday(dateKey);
  };

  return (
    <div className="flex flex-col gap-2">
      <SectionLabel>{t("reservations.form.when")}</SectionLabel>

      <div className="grid grid-cols-7 gap-1.5">
        {dayKeys.map((dateKey, index) => {
          const active = dateKey === slot.dateKey;
          return (
            <button
              key={dateKey}
              type="button"
              aria-pressed={active}
              onClick={() => onDayChange(dateKey)}
              className={`${tileClass(active, false)} h-[50px] flex-col gap-0.5 px-0`}
            >
              <span
                className={`max-w-full truncate text-[10.5px] font-semibold leading-[1.2] ${
                  active || isWeekend(dateKey) ? "text-brand-deep-ink" : "text-muted"
                }`}
              >
                {dayLabel(dateKey, index)}
              </span>
              <span className="text-[15px] leading-[1.1]">{parseDateKey(dateKey).getDate()}</span>
            </button>
          );
        })}
      </div>

      <div className="relative">
        <button
          type="button"
          onClick={() => setCalendarOpen(true)}
          className={`flex h-10 w-full cursor-pointer items-center justify-between gap-2 rounded-[10px] border px-3.5 text-[13.5px] font-semibold transition-colors duration-150 ${
            isOtherDate
              ? "border-brand bg-brand-tint text-brand-deep-ink"
              : "border-shade-3 bg-surface text-ink hover:bg-fill"
          }`}
        >
          <span className="flex items-center gap-2">
            <CalendarOutlined className="text-[13px] text-body" />
            {isOtherDate ? format.longDay(slot.dateKey) : t("reservations.form.otherDate")}
          </span>
          <DownOutlined className="text-[10px] text-muted" />
        </button>
        {/* Невидимото поле само закотвя календара под бутона. */}
        <ConfigProvider
          theme={{
            token: {
              colorPrimary: "#D97706",
              // Token математиката на antd не разбира var() → условна стойност.
              controlItemBgActive: isDark ? "#382a12" : "#FFF4E0",
            },
          }}
        >
          <DatePicker
            locale={pickerLocale}
            open={calendarOpen}
            onOpenChange={setCalendarOpen}
            value={dayjs(parseDateKey(slot.dateKey))}
            allowClear={false}
            placement="bottomLeft"
            disabledDate={(date) => date.format("YYYY-MM-DD") < firstDateKeyOf(nowMs, originalSlot)}
            onChange={(value) => {
              setCalendarOpen(false);
              if (value) onDayChange(value.format("YYYY-MM-DD"));
            }}
            className="!pointer-events-none !absolute !inset-0 !opacity-0"
            tabIndex={-1}
            aria-hidden
          />
        </ConfigProvider>
      </div>

      {dayClosed ? (
        <div role="alert" className="text-[12.5px] font-semibold text-danger-ink">
          {t("reservations.form.dayClosed")}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-6 gap-1.5">
            {shiftHours.map((entry) => {
              const disabled = isHourDisabled(entry);
              const selected = isSelectedHour(entry);
              return (
                <button
                  key={`${entry.dateKey}-${entry.hour}`}
                  type="button"
                  disabled={disabled}
                  aria-pressed={selected}
                  onClick={() => onHourChange(entry.hour, entry.dateKey)}
                  className={`${tileClass(selected, disabled)} h-9`}
                >
                  {pad(entry.hour)}
                </button>
              );
            })}
          </div>
          {nightHours.length > 0 && (
            <div className="text-[12px] leading-[1.4] text-muted">
              {t("reservations.form.afterMidnightHint", {
                from: pad(nightHours[0].hour),
                to: pad(nightHours[nightHours.length - 1].hour),
                day: `${format.shortWeekday(nightHours[0].dateKey)}, ${format.shortDate(nightHours[0].dateKey)}`,
              })}
            </div>
          )}

          <div className="grid grid-cols-4 gap-1.5">
            {FORM_MINUTES.map((minute) => {
              const disabled = !isFreeSlot(slot.dateKey, selectedHour * 60 + minute);
              return (
                <button
                  key={minute}
                  type="button"
                  disabled={disabled}
                  aria-pressed={minute === selectedMinute}
                  onClick={() => onMinuteChange(minute)}
                  className={`${tileClass(minute === selectedMinute, disabled)} h-9`}
                >
                  :{pad(minute)}
                </button>
              );
            })}
          </div>
          {slotOutsideHours && (
            <div role="alert" className="text-[12.5px] font-semibold text-danger-ink">
              {t("reservations.form.slotOutsideHours")}
            </div>
          )}
          {slotPast && (
            <div role="alert" className="text-[12.5px] font-semibold text-danger-ink">
              {t("reservations.form.slotPast")}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default WhenSection;
